import ru from 'r3ui-lang-front/ru.json' with { type: 'json' };
import en from 'r3ui-lang-front/en.json' with { type: 'json' };
import es from 'r3ui-lang-front/es.json' with { type: 'json' };
import fr from 'r3ui-lang-front/fr.json' with { type: 'json' };
import pt from 'r3ui-lang-front/pt.json' with { type: 'json' };
import kk from 'r3ui-lang-front/kk.json' with { type: 'json' };
import vi from 'r3ui-lang-front/vi.json' with { type: 'json' };
import zh from 'r3ui-lang-front/zh.json' with { type: 'json' };
import uz from 'r3ui-lang-front/uz.json' with { type: 'json' };
import he from 'r3ui-lang-front/he.json' with { type: 'json' };
import { createI18n } from 'vue-i18n';
import type { I18nOptions } from 'vue-i18n';
import Cookies from 'js-cookie';

const messages: I18nOptions['messages'] = Object.assign({
  ru,
  en,
  es,
  fr,
  pt,
  kk,
  vi,
  zh,
  uz,
  he,
});

// Пока установленный r3ui-lang-front без этих ключей, vue-i18n показывает сырой ключ.
const mapErrorFallback = {
  ru: {
    no_coordinates: 'У организации нет координат',
    geocoding_failed: 'Не удалось определить координаты организации по адресу',
  },
  en: {
    no_coordinates: 'The organization has no coordinates',
    geocoding_failed:
      'Could not resolve the organization address to coordinates',
  },
} as const;

const herriotColumnFallback = {
  ru: {
    herriot_integrations: 'Интеграции Хорриот',
    herriot_number: 'Номер Хорриот',
    herriot_guid: 'GUID Хорриот',
    integrated: 'Интегрировано',
    not_integrated: 'Не интегрировано',
  },
  en: {
    herriot_integrations: 'Herriot integrations',
    herriot_number: 'Herriot number',
    herriot_guid: 'Herriot GUID',
    integrated: 'Integrated',
    not_integrated: 'Not integrated',
  },
  es: {
    herriot_integrations: 'Integraciones Herriot',
    herriot_number: 'Número Herriot',
    herriot_guid: 'GUID Herriot',
    integrated: 'Integrado',
    not_integrated: 'No integrado',
  },
  fr: {
    herriot_integrations: 'Intégrations Herriot',
    herriot_number: 'Numéro Herriot',
    herriot_guid: 'GUID Herriot',
    integrated: 'Intégré',
    not_integrated: 'Non intégré',
  },
  pt: {
    herriot_integrations: 'Integrações Herriot',
    herriot_number: 'Número Herriot',
    herriot_guid: 'GUID Herriot',
    integrated: 'Integrado',
    not_integrated: 'Não integrado',
  },
  kk: {
    herriot_integrations: 'Хорриот интеграциялары',
    herriot_number: 'Хорриот нөмірі',
    herriot_guid: 'Хорриот GUID',
    integrated: 'Интеграцияланған',
    not_integrated: 'Интеграцияланбаған',
  },
  vi: {
    herriot_integrations: 'Tích hợp Herriot',
    herriot_number: 'Số Herriot',
    herriot_guid: 'GUID Herriot',
    integrated: 'Đã tích hợp',
    not_integrated: 'Chưa tích hợp',
  },
  zh: {
    herriot_integrations: 'Herriot 集成',
    herriot_number: 'Herriot 编号',
    herriot_guid: 'Herriot GUID',
    integrated: '已集成',
    not_integrated: '未集成',
  },
  uz: {
    herriot_integrations: 'Herriot integratsiyalari',
    herriot_number: 'Herriot raqami',
    herriot_guid: 'Herriot GUID',
    integrated: 'Integratsiya qilingan',
    not_integrated: 'Integratsiya qilinmagan',
  },
  he: {
    herriot_integrations: 'אינטגרציות Herriot',
    herriot_number: 'מספר Herriot',
    herriot_guid: 'GUID Herriot',
    integrated: 'משולב',
    not_integrated: 'לא משולב',
  },
} as const;

for (const locale of ['ru', 'en'] as const) {
  if (!messages?.[locale])
    throw new Error(`Отсутствует локализация: ${locale}`);
  const pack = messages[locale] as {
    map?: { errors?: Record<string, string> };
  };
  const errors = ((pack.map ??= {}).errors ??= {});
  for (const [key, value] of Object.entries(mapErrorFallback[locale])) {
    if (!errors[key]) errors[key] = value;
  }
}

for (const locale of [
  'ru',
  'en',
  'es',
  'fr',
  'pt',
  'kk',
  'vi',
  'zh',
  'uz',
  'he',
] as const) {
  if (!messages?.[locale])
    throw new Error(`Отсутствует локализация: ${locale}`);
  const pack = messages[locale] as {
    table_columns?: Record<string, string>;
  };
  const columns = (pack.table_columns ??= {});
  for (const [key, value] of Object.entries(herriotColumnFallback[locale])) {
    if (!columns[key]) columns[key] = value;
  }
}

const savedLocale = Cookies.get('i18n_redirected') as string | undefined;
type Locale =
  'ru' | 'en' | 'es' | 'fr' | 'pt' | 'kk' | 'vi' | 'zh' | 'uz' | 'he';
const validLocale: Locale = (
  ['ru', 'en', 'es', 'fr', 'pt', 'kk', 'vi', 'zh', 'uz', 'he'].includes(
    savedLocale || ''
  )
    ? savedLocale
    : 'ru'
) as Locale;

const options: I18nOptions = {
  legacy: false,
  locale: validLocale,
  fallbackLocale: 'en',
  messages,
  missingWarn: false,
  fallbackWarn: false,
};
const i18n = createI18n({ ...options });

export const { t, locale, availableLocales } = i18n.global;
export const translate = (str: string) => {
  if (!str) return str;
  return t(str);
};

export default defineI18nConfig(() => {
  return {
    ...options,
    defaultLocale: 'ru',
    detectBrowserLanguage: {
      useCookie: true,
      cookieKey: 'i18n_redirected',
      redirectOn: 'root', // recommended
    },
  };
});
